import { INDENT_ATTRS, INDENT_EM_PER_LEVEL, INDENT_FLAG_ATTR, LABEL_BOX_ATTR } from '@root/helpers/editor';
import type { Attribute } from '@tiptap/core';
import { Extension, Mark } from '@tiptap/core';
import type { SingleCommands } from '@tiptap/react';
import { getNodeAttributes } from '@tiptap/react';

export interface IndentOptions {
  types: string[];
}

type IndentModel = Partial<Record<keyof typeof INDENT_ATTRS, number>>;

const INDENTED_TYPES = ['paragraph', 'heading'];

const LabelBox = Mark.create({
  name: 'labelBox',
  inclusive: false,

  parseHTML() {
    return [{ tag: `span[${LABEL_BOX_ATTR}]` }];
  },

  renderHTML() {
    return ['span', { [LABEL_BOX_ATTR]: '' }, 0];
  },
});

const inEm = (levels: number) => `${levels * INDENT_EM_PER_LEVEL}em`;

const parseLevels = (element: HTMLElement, name: string) => parseInt(element.getAttribute(name) ?? '', 10) || 0;

const indentAttributes = {
  indentLevel: {
    default: 0,
    parseHTML: (element: HTMLElement) =>
      element.getAttribute(INDENT_FLAG_ATTR) ? parseLevels(element, INDENT_ATTRS.indentLevel) : 0,
    renderHTML: ({ indentLevel = 0, hanging = 0 }: IndentModel) => {
      if (!indentLevel) {
        return {};
      }
      const hang = Math.min(hanging, indentLevel);
      const paddingLeft = `padding-left: ${inEm(indentLevel)}`;

      return {
        style: hang > 0 ? `${paddingLeft}; text-indent: -${inEm(hang)}` : paddingLeft,
        [INDENT_ATTRS.indentLevel]: indentLevel,
        [INDENT_FLAG_ATTR]: true,
        ...(hang > 0 ? { [INDENT_ATTRS.hanging]: hang } : {}),
      };
    },
  },
  hanging: {
    default: 0,
    parseHTML: (element: HTMLElement) => parseLevels(element, INDENT_ATTRS.hanging),
    renderHTML: () => ({}),
  },
} satisfies Record<keyof typeof INDENT_ATTRS, Attribute>;

export const Indent = Extension.create<IndentOptions>({
  name: 'indent',
  priority: 97,

  addOptions() {
    return {
      types: [
        'table', // parsed and saved only; Tab moves between its cells
        ...INDENTED_TYPES,
      ],
    };
  },

  addExtensions() {
    return [LabelBox];
  },

  addGlobalAttributes() {
    return [{ types: this.options.types, attributes: indentAttributes }];
  },

  addCommands() {
    const indentBy =
      (delta: number) =>
      () =>
      ({ commands }: { commands: SingleCommands }) => {
        if (this.editor.isActive('listItem')) {
          return delta > 0 ? commands.indentOrderedListRight() : commands.indentOrderedListLeft();
        }
        return INDENTED_TYPES.every((type) => {
          const { indentLevel = 0, hanging = 0 }: IndentModel = getNodeAttributes(this.editor.state, type);
          const nextLevel = Math.max(0, indentLevel + delta);
          return commands.updateAttributes(type, { indentLevel: nextLevel, hanging: Math.min(hanging, nextLevel) });
        });
      };

    return {
      indentLeftOrGoToPreviousCell:
        () =>
        ({ commands }: { commands: SingleCommands }) =>
          commands.goToPreviousCell() || commands.indentLeft(),
      indentRightOrGoToNextCell:
        () =>
        ({ commands }: { commands: SingleCommands }) =>
          commands.goToNextCell() || commands.indentRight(),
      indentRight: indentBy(1),
      indentLeft: indentBy(-1),
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        this.editor.commands.indentRightOrGoToNextCell();
        return this.editor.commands.focus();
      },
      'Shift-Tab': () => {
        this.editor.commands.indentLeftOrGoToPreviousCell();
        return this.editor.commands.focus();
      },
    };
  },
});
